import { Box, Button, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Plus, Trash } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../../store/storeAuth';
import { ThirdPartyIntegrationService } from '../../../../service/ThirdPartyIntegrationService';
import Label from '../../../UI/Label/Label';

const OPERATORS_BY_TYPE: Record<string, Array<{ label: string; value: string }>> = {
  checkbox: [
    { label: 'Equals', value: 'equals' },
    { label: 'Does not equal', value: 'does_not_equal' },
  ],
  date: [
    { label: 'After', value: 'after' },
    { label: 'Before', value: 'before' },
    { label: 'On or after', value: 'on_or_after' },
    { label: 'On or before', value: 'on_or_before' },
    { label: 'Is empty', value: 'is_empty' },
    { label: 'Is not empty', value: 'is_not_empty' },
  ],
  rich_text: [{ label: 'Contains', value: 'contains' }],
  number: [
    { label: 'Equals', value: 'equals' },
    { label: 'Greater than', value: 'greater_than' },
    { label: 'Less than', value: 'less_than' },
  ],
  select: [
    { label: 'Equals', value: 'equals' },
    { label: 'Contains', value: 'contains' },
  ],
  multi_select: [
    { label: 'Equals', value: 'equals' },
    { label: 'Contains', value: 'contains' },
  ],
  status: [
    { label: 'Equals', value: 'equals' },
    { label: 'Does not equal', value: 'does_not_equal' },
    { label: 'Is empty', value: 'is_empty' },
    { label: 'Is not empty', value: 'is_not_empty' },
  ],
};

export interface NotionFilterCondition {
  property: string;
  propertyType: string;
  operator: string;
  value: unknown;
}

interface NotionFilterBuilderProps {
  databaseId: string;
  value: { and?: NotionFilterCondition[] } | NotionFilterCondition[] | undefined;
  onChange: (value: { and?: NotionFilterCondition[] } | undefined) => void;
  disabled?: boolean;
  nodeData?: Record<string, unknown>;
}

interface SchemaProperty {
  id: string;
  name: string;
  type: string;
  options?: Array<{ id: string; name: string }>;
}

export const NotionFilterBuilder: React.FC<NotionFilterBuilderProps> = ({
  databaseId,
  value,
  onChange,
  disabled,
  nodeData,
}) => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';
  const [schema, setSchema] = useState<SchemaProperty[]>([]);
  const [loading, setLoading] = useState(false);

  const conditions: NotionFilterCondition[] = Array.isArray(value)
    ? value
    : value?.and ?? [];

  const context: Record<string, unknown> = {};
  if (nodeData?.integrationId) context.integrationId = nodeData.integrationId;
  if (nodeData?.connectedByUserId) context.connectedByUserId = nodeData.connectedByUserId;

  useEffect(() => {
    if (!databaseId || disabled) return;
    let cancelled = false;
    setLoading(true);
    ThirdPartyIntegrationService.getDatabaseSchema('notion', databaseId, context)
      .then(res => {
        if (!cancelled && res?.data?.properties) {
          setSchema(res.data.properties);
        }
      })
      .catch(() => {
        if (!cancelled) setSchema([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [databaseId, disabled, nodeData?.integrationId, nodeData?.connectedByUserId]);

  const updateConditions = useCallback(
    (next: NotionFilterCondition[]) => {
      if (next.length === 0) onChange(undefined);
      else if (next.length === 1) onChange(next[0]);
      else onChange({ and: next });
    },
    [onChange],
  );

  const addRow = useCallback(() => {
    const first = schema[0];
    updateConditions([
      ...conditions,
      {
        property: first?.name ?? first?.id ?? '',
        propertyType: first?.type ?? 'rich_text',
        operator: 'equals',
        value: '',
      },
    ]);
  }, [conditions, schema, updateConditions]);

  const removeRow = useCallback(
    (index: number) => {
      const next = conditions.filter((_, i) => i !== index);
      updateConditions(next);
    },
    [conditions, updateConditions],
  );

  const updateRow = useCallback(
    (index: number, patch: Partial<NotionFilterCondition>) => {
      const next = conditions.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      );
      updateConditions(next);
    },
    [conditions, updateConditions],
  );

  if (!databaseId) return null;
  if (loading) return <Typography variant="caption">Loading schema...</Typography>;

  const selectSx = {
    minWidth: 140,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#475467' : '#D0D5DD' },
    bgcolor: isDark ? '#101828' : '#F9FAFB',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Label
        labelName="Filter conditions"
        isAdvanced
        labelStyle={{
          fontSize: '14px',
          color: isDark ? '#D0D5DD' : '#101828',
          fontWeight: 500,
        }}
      />
      {conditions.map((row, index) => {
        const prop = schema.find(p => p.name === row.property || p.id === row.property);
        const propType = prop?.type ?? row.propertyType ?? 'rich_text';
        const operators = OPERATORS_BY_TYPE[propType] ?? OPERATORS_BY_TYPE.rich_text;
        const needsValue =
          !['is_empty', 'is_not_empty'].includes(row.operator as string);
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            }}
          >
            <Select
              size="small"
              value={row.property || ''}
              onChange={e => {
                const p = schema.find(
                  s => s.name === e.target.value || s.id === e.target.value,
                );
                updateRow(index, {
                  property: p?.name ?? e.target.value,
                  propertyType: p?.type ?? 'rich_text',
                  operator: (OPERATORS_BY_TYPE[p?.type ?? 'rich_text'] ?? OPERATORS_BY_TYPE.rich_text)[0]?.value ?? 'equals',
                  value: '',
                });
              }}
              displayEmpty
              disabled={disabled}
              sx={selectSx}
            >
              {schema.map(s => (
                <MenuItem key={s.id} value={s.name}>
                  {s.name} ({s.type})
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={row.operator || 'equals'}
              onChange={e => updateRow(index, { operator: e.target.value })}
              disabled={disabled}
              sx={{ ...selectSx, minWidth: 120 }}
            >
              {operators.map(op => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
            {needsValue && (
              <>
                {propType === 'checkbox' ? (
                  <Select
                    size="small"
                    value={row.value === true || row.value === 'true' ? 'true' : 'false'}
                    onChange={e =>
                      updateRow(index, { value: e.target.value === 'true' })
                    }
                    disabled={disabled}
                    sx={{ ...selectSx, minWidth: 80 }}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                ) : prop?.options?.length ? (
                  <Select
                    size="small"
                    value={String(row.value ?? '')}
                    onChange={e => updateRow(index, { value: e.target.value })}
                    disabled={disabled}
                    displayEmpty
                    sx={{ ...selectSx, minWidth: 120 }}
                  >
                    {prop.options.map(opt => (
                      <MenuItem key={opt.id} value={opt.name}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField
                    size="small"
                    placeholder="Value"
                    value={row.value ?? ''}
                    onChange={e => updateRow(index, { value: e.target.value })}
                    disabled={disabled}
                    sx={{ width: 120, '& .MuiOutlinedInput-root': { bgcolor: isDark ? '#101828' : '#F9FAFB' } }}
                  />
                )}
              </>
            )}
            <IconButton
              size="small"
              onClick={() => removeRow(index)}
              disabled={disabled}
              aria-label="Remove condition"
            >
              <Trash size={16} />
            </IconButton>
          </Box>
        );
      })}
      <Button
        className="nodrag"
        size="small"
        startIcon={<Plus size={16} />}
        onClick={addRow}
        disabled={disabled || schema.length === 0}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        Add condition
      </Button>
    </Box>
  );
};
